import React, { useEffect, useState } from 'react';
import { Order } from '../types';
import { api } from '../constants';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import Toast from '../components/Toast';

interface OrderDetailProps {
  orderId: string;
  onNavigate: (page: string) => void;
}

const OrderDetail: React.FC<OrderDetailProps> = ({ orderId, onNavigate }) => {
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const { orders } = useCart();
  const { isAdmin } = useAuth();

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
      setNotification({ message, type });
  };
  
  const [adminControls, setAdminControls] = useState({
      status: '',
      note: ''
  });

  useEffect(() => {
    const fetchOrder = async () => {
      // 1. Try finding in context first (immediate consistency for just-created orders)
      const contextOrder = orders.find(o => o.id === orderId);
      if (contextOrder) {
        setOrder(contextOrder);
        setAdminControls({ status: contextOrder.status, note: contextOrder.adminNote || '' });
        setLoading(false);
        return;
      }

      // 2. Try API/LocalStorage
      try {
        const user = JSON.parse(localStorage.getItem('piggy_dental_user') || '{}');
        // Admin uses special logic to fetch any order (via scan/list usually)
        // For simplicity we fetch all and find, or we could have a getOrderById API
        const fetchedOrders = await api.getOrders(user.id);
        const found = fetchedOrders.find((o: Order) => o.id === orderId);
        if (found) {
            setOrder(found);
            setAdminControls({ status: found.status, note: found.adminNote || '' });
        }
      } catch (e) {
        console.error("Failed to load order", e);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderId, orders]);

  const handleUpdateStatus = async () => {
    if (!order) return;
    setUpdating(true);
    try {
        const baseUrl = (process.env.REACT_APP_API_ENDPOINT || '').replace(/\/$/, '');
        const response = await fetch(`${baseUrl}/orders`, {
            method: 'PUT',
            headers: { 
                'Content-Type': 'application/json', 
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}` 
            },
            body: JSON.stringify({ 
                orderId: order.id, 
                status: adminControls.status, 
                adminNote: adminControls.note 
            })
        });
        if (response.ok) {
            setOrder({ ...order, status: adminControls.status as any, adminNote: adminControls.note });
            showToast("Order updated successfully");
        }
    } catch (e: any) {
        showToast(e.message || "Update failed", "error");
    } finally {
        setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        {notification && (
            <Toast 
                message={notification.message} 
                type={notification.type} 
                onClose={() => setNotification(null)} 
            />
        )}
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
        {notification && (
            <Toast 
                message={notification.message} 
                type={notification.type} 
                onClose={() => setNotification(null)} 
            />
        )}
        <div className="text-6xl mb-4">🔍</div>
        <h2 className="text-2xl font-bold text-slate-900 mb-4">Order Not Found</h2>
        <button 
            onClick={() => onNavigate(isAdmin ? 'admin' : 'orders')}
            className="text-blue-600 hover:text-blue-800 font-semibold"
        >
            Return to {isAdmin ? 'Admin Panel' : 'Orders'}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
      {notification && (
          <Toast 
              message={notification.message} 
              type={notification.type} 
              onClose={() => setNotification(null)} 
          />
      )}
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 flex justify-between items-center print:hidden">
          <button
            onClick={() => onNavigate(isAdmin ? 'admin' : 'orders')}
            className="flex items-center text-slate-500 hover:text-slate-700 font-bold uppercase text-[10px] tracking-widest bg-white px-4 py-2 rounded-xl shadow-sm border border-slate-100 transition-all"
          >
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to {isAdmin ? 'Fulfillment' : 'Orders'}
          </button>
          <div className="flex gap-3">
              <button
                onClick={() => window.print()}
                className="flex items-center px-4 py-2 bg-white border border-slate-200 rounded-xl shadow-sm hover:bg-slate-50 text-slate-700 font-black uppercase text-[10px] tracking-widest transition-all"
              >
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Print Invoice
              </button>
          </div>
        </div>

        {isAdmin && (
            <div className="mb-8 bg-slate-900 rounded-[2rem] p-8 shadow-2xl text-white animate-fade-in print:hidden">
                <div className="flex items-center gap-4 mb-6">
                    <span className="text-3xl">🛠️</span>
                    <div>
                        <h2 className="text-xl font-black uppercase tracking-tight">Admin Controls</h2>
                        <p className="text-slate-400 text-xs font-medium">Override status or add internal notes for fulfillment.</p>
                    </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500">Update Shipment Status</label>
                        <select 
                            value={adminControls.status}
                            onChange={(e) => setAdminControls({...adminControls, status: e.target.value})}
                            className="w-full bg-slate-800 border-none rounded-xl px-6 py-4 font-black uppercase text-xs tracking-widest focus:ring-4 focus:ring-blue-500/20 outline-none cursor-pointer"
                        >
                            <option value="ORDERED">Ordered</option>
                            <option value="DELIVERING">Delivering</option>
                            <option value="COMPLETED">Completed</option>
                            <option value="CANCELLED">Cancelled</option>
                        </select>
                    </div>
                    <div className="space-y-4">
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500">Internal Fulfillment Note</label>
                        <textarea 
                            value={adminControls.note}
                            onChange={(e) => setAdminControls({...adminControls, note: e.target.value})}
                            placeholder="Add notes for the warehouse..."
                            className="w-full bg-slate-800 border-none rounded-xl px-6 py-4 text-sm font-medium focus:ring-4 focus:ring-blue-500/20 outline-none resize-none h-14"
                        />
                    </div>
                </div>
                
                <button 
                    onClick={handleUpdateStatus}
                    disabled={updating}
                    className="mt-8 w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 py-4 rounded-xl font-black uppercase text-xs tracking-[0.2em] transition-all shadow-xl shadow-blue-500/10"
                >
                    {updating ? 'Updating Database...' : 'Commit Changes to Order'}
                </button>
            </div>
        )}

        <div className="bg-white rounded-[2.5rem] shadow-xl overflow-hidden print:shadow-none border border-slate-100">
          {/* Header */}
          <div className="bg-slate-50 p-8 sm:p-12 print:bg-white print:text-black print:border-b-2 print:border-black">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-3xl font-black uppercase tracking-wider mb-2">Invoice</h1>
                <p className="opacity-80 font-mono">#{order.id}</p>
              </div>
              <div className="text-right">
                <div className="flex items-center justify-end space-x-2 mb-2">
                   <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center print:hidden">
                      <span className="text-xl">🦷</span>
                   </div>
                   <span className="text-xl font-bold tracking-tight">Piggy Dental</span>
                </div>
                <p className="text-sm opacity-70">1230 W Georgia St</p>
                <p className="text-sm opacity-70">Vancouver, BC V6E 3C9</p>
                        <div>
                            <h3 className="text-xs font-black uppercase tracking-widest opacity-50 mb-1">Email Support</h3>
                            <p className="text-sm opacity-70">info@piggyll.com</p>
                        </div>
              </div>
            </div>
            
            <div className="mt-12 grid grid-cols-2 gap-8">
              <div>
                <h3 className="text-xs font-bold uppercase tracking-widest opacity-60 mb-2">Billed To</h3>
                <p className="font-bold text-lg">{order.businessName}</p>
                {/* Mock address for now as it's not strictly in Order type yet but we can add or assume */}
                <p className="opacity-80 text-sm mt-1">
                    {order.shippingAddress?.street}<br/>
                    {order.shippingAddress?.city}, {order.shippingAddress?.state} {order.shippingAddress?.postalCode}<br/>
                    {order.shippingAddress?.country}
                </p>
              </div>
              <div className="text-right">
                <div className="mb-4">
                    <h3 className="text-xs font-bold uppercase tracking-widest opacity-60 mb-1">Date Issued</h3>
                    <p className="font-medium">{new Date(order.date).toLocaleDateString()}</p>
                </div>
                <div>
                    <h3 className="text-xs font-bold uppercase tracking-widest opacity-60 mb-1">Payment Method</h3>
                    <p className="font-medium">{order.paymentMethod}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="p-8 sm:p-12">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="pb-4 font-black text-slate-900 uppercase tracking-wider text-xs">Item Description</th>
                  <th className="pb-4 font-black text-slate-900 uppercase tracking-wider text-xs text-center">Qty</th>
                  <th className="pb-4 font-black text-slate-900 uppercase tracking-wider text-xs text-right">Price</th>
                  <th className="pb-4 font-black text-slate-900 uppercase tracking-wider text-xs text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="text-sm text-slate-600">
                {order.items.map((item, index) => (
                  <tr key={index} className="border-b border-slate-50 last:border-0">
                    <td className="py-4">
                      <div className="font-bold text-slate-900">{item.name}</div>
                      <div className="text-xs text-slate-400 mt-1">{item.category}</div>
                    </td>
                    <td className="py-4 text-center font-mono">{item.quantity}</td>
                    <td className="py-4 text-right font-mono">${item.price.toFixed(2)}</td>
                    <td className="py-4 text-right font-bold text-slate-900 font-mono">
                      ${(item.price * item.quantity).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mt-12 flex justify-end">
              <div className="w-full sm:w-1/2 space-y-3">
                <div className="flex justify-between text-slate-500">
                  <span>Subtotal</span>
                  <span className="font-mono">${order.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-slate-500">
                  <span>Tax (12%)</span>
                  <span className="font-mono">${order.tax.toFixed(2)}</span>
                </div>
                <div className="border-t border-slate-200 pt-3 flex justify-between items-center">
                  <span className="font-black text-slate-900 uppercase tracking-widest">Total Due</span>
                  <span className="font-black text-2xl text-blue-600 font-mono">${order.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
            <div className="mt-12 text-center">
                <p className="text-slate-400 text-xs font-bold uppercase tracking-widest">
                    Thank you for your business! For any questions, please contact info@piggyll.com
                </p>
            </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;
